import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';
import { Menu, X, ChevronDown, Phone, ArrowRight } from 'lucide-react';
import { Button } from '../ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';

const LOGO_URL = "/calyvra-logo-horizontal.webp";

export const Navbar = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const location = useLocation();
  const { t } = useTranslation();

  const navLinks = [
    { id: 'home', name: t('nav.home'), path: '/' },
    { id: 'community-organizations', name: t('nav.communityOrganizations'), path: '/community-organizations' },
    {
      id: 'coverage',
      name: t('nav.coverage'),
      dropdown: true,
      items: [
        { name: t('nav.healthPlans'), path: '/health-plans' },
        { name: t('nav.dentalVision'), path: '/dental-vision' },
        { name: t('nav.medicare'), path: '/medicare' },
        { name: t('nav.smallBusiness'), path: '/small-business' },
      ]
    },
    {
      id: 'enrollment',
      name: t('nav.enrollment'),
      dropdown: true,
      items: [
        { heading: t('nav.enrollmentAca') },
        { name: t('nav.openEnrollment'), path: '/open-enrollment' },
        { name: t('nav.specialEnrollment'), path: '/special-enrollment' },
        { heading: t('nav.enrollmentMedicare') },
        { name: t('nav.medicarePlans'), path: '/medicare' },
        { name: t('nav.medicareEnrollment'), path: '/medicare-enrollment' },
      ]
    },
    { id: 'about', name: t('nav.aboutUs'), path: '/about' },
    { id: 'career', name: t('nav.career'), path: '/work-with-us' },
  ];

  const isActive = (path) => location.pathname === path;

  // Lock background scroll while the mobile menu is open so only the menu scrolls.
  useEffect(() => {
    document.body.style.overflow = mobileMenuOpen ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [mobileMenuOpen]);

  return (
    <nav className="fixed top-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-md border-b border-slate-100" data-testid="navbar">
      <div className="container-custom">
        <div className="relative flex items-center justify-between h-28">
          {/* Logo */}
          <Link to="/" className="flex items-center mx-auto lg:mx-0" data-testid="navbar-logo">
            <img 
              src={LOGO_URL} 
              alt="Calyvra Insurance" 
              className="h-14 lg:h-16 w-auto object-contain"
            />
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center gap-1">
            {navLinks.map((link) => (
              link.dropdown ? (
                <DropdownMenu key={link.id}>
                  <DropdownMenuTrigger asChild>
                    <button 
                      className="flex items-center gap-1 px-4 py-2 text-slate-600 hover:text-slate-900 font-medium transition-colors"
                      data-testid={`nav-dropdown-${link.id}`}
                    >
                      {link.name}
                      <ChevronDown className="h-4 w-4" />
                    </button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="start" className="w-56">
                    {link.items.map((item) => (
                      item.heading ? (
                        <DropdownMenuLabel key={item.heading} className="text-[11px] uppercase tracking-wide text-slate-400 font-semibold">
                          {item.heading}
                        </DropdownMenuLabel>
                      ) : (
                        <DropdownMenuItem key={item.path} asChild>
                          <Link 
                            to={item.path} 
                            className={`w-full ${isActive(item.path) ? 'text-sky-500' : ''}`}
                            data-testid={`nav-link-${item.path.slice(1)}`}
                          >
                            {item.name}
                          </Link>
                        </DropdownMenuItem>
                      )
                    ))}
                  </DropdownMenuContent>
                </DropdownMenu>
              ) : (
                <Link
                  key={link.path}
                  to={link.path}
                  className={`px-4 py-2 font-medium transition-colors ${
                    isActive(link.path) 
                      ? 'text-sky-500' 
                      : 'text-slate-600 hover:text-slate-900'
                  }`}
                  data-testid={`nav-link-${link.id}`}
                >
                  {link.name}
                </Link>
              )
            ))}
          </div>

          {/* CTA Button */}
          <div className="hidden lg:flex items-center gap-5">
            <a
              href="tel:+13054323557"
              className="hidden xl:flex items-center gap-2 text-slate-700 hover:text-sky-600 font-semibold transition-colors"
              data-testid="navbar-phone"
            >
              <span className="flex h-9 w-9 items-center justify-center rounded-full bg-sky-50 text-sky-600">
                <Phone className="h-4 w-4" />
              </span>
              (305) 432-3557
            </a>
            <Link to="/contact">
              <Button
                className="group bg-sky-500 hover:bg-sky-600 text-white h-12 px-7 rounded-full font-semibold shadow-lg shadow-sky-500/30 hover:shadow-xl hover:shadow-sky-500/40 hover:-translate-y-0.5 transition-all duration-300"
                data-testid="navbar-cta"
              >
                {t('nav.contact')}
                <ArrowRight className="h-4 w-4 ml-2 transition-transform group-hover:translate-x-0.5" />
              </Button>
            </Link>
          </div>

          {/* Mobile Menu Button */}
          <div className="lg:hidden absolute right-0 top-1/2 -translate-y-1/2 flex items-center gap-2">
            <button
              className="p-2 text-slate-600 hover:text-slate-900"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              data-testid="mobile-menu-toggle"
            >
              {mobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Menu */}
      <AnimatePresence>
        {mobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="lg:hidden bg-white border-b border-slate-100 max-h-[calc(100vh-7rem)] overflow-y-auto overscroll-contain"
            data-testid="mobile-menu"
          >
            <div className="container-custom py-4 space-y-2">
              {navLinks.map((link) => (
                link.dropdown ? (
                  <div key={link.id} className="space-y-1">
                    <div className="px-4 py-2 text-sm font-semibold text-slate-400 uppercase tracking-wide">
                      {link.name}
                    </div>
                    {link.items.map((item) => (
                      item.heading ? (
                        <div key={item.heading} className="px-6 pt-3 pb-1 text-[11px] font-semibold text-slate-400 uppercase tracking-wide">
                          {item.heading}
                        </div>
                      ) : (
                        <Link
                          key={item.path}
                          to={item.path}
                          onClick={() => setMobileMenuOpen(false)}
                          className={`block px-4 py-2 pl-6 font-medium transition-colors ${
                            isActive(item.path)
                              ? 'text-sky-500 bg-sky-50 rounded-lg'
                              : 'text-slate-600 hover:text-slate-900 hover:bg-slate-50 rounded-lg'
                          }`}
                          data-testid={`mobile-nav-link-${item.path.slice(1)}`}
                        >
                          {item.name}
                        </Link>
                      )
                    ))}
                  </div>
                ) : (
                  <Link
                    key={link.path}
                    to={link.path}
                    onClick={() => setMobileMenuOpen(false)}
                    className={`block px-4 py-2 font-medium transition-colors ${
                      isActive(link.path)
                        ? 'text-sky-500 bg-sky-50 rounded-lg'
                        : 'text-slate-600 hover:text-slate-900 hover:bg-slate-50 rounded-lg'
                    }`}
                    data-testid={`mobile-nav-link-${link.id}`}
                  >
                    {link.name}
                  </Link>
                )
              ))}
              <div className="pt-4">
                <a
                  href="tel:+13054323557"
                  className="flex items-center justify-center gap-2 mb-3 py-2 text-slate-700 font-semibold"
                  data-testid="mobile-navbar-phone"
                >
                  <Phone className="h-4 w-4 text-sky-500" />
                  (305) 432-3557
                </a>
                <Link to="/contact" onClick={() => setMobileMenuOpen(false)}>
                  <Button className="group w-full bg-sky-500 hover:bg-sky-600 text-white h-12 rounded-full font-semibold shadow-lg shadow-sky-500/30 transition-all duration-300" data-testid="mobile-navbar-cta">
                    {t('nav.contact')}
                    <ArrowRight className="h-4 w-4 ml-2 transition-transform group-hover:translate-x-0.5" />
                  </Button>
                </Link>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </nav>
  );
};
